import { useEffect, useRef, useState } from "react";
import { Box, Download, LoaderCircle, X } from "lucide-react";
import { modelFormatLabel } from "../shared/model-files";
import type { ModelAssetInfo } from "./model-assets";
import type { ModelStats, ModelView } from "./model-engine";

function readableError(error: unknown) {
  const message =
    error instanceof Error ? error.message : "Model preview failed.";
  if (/draco/i.test(message))
    return "This GLB uses Draco compression. Export a standard GLB for this viewer.";
  if (
    /texture|image/i.test(message) &&
    /unsupported|decode|format/i.test(message)
  )
    return "A model texture could not be decoded. The original file is still available.";
  return message;
}

export function ModelViewer({
  model,
  close,
}: {
  model: ModelAssetInfo;
  close: () => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const controller = useRef<
    | {
        stats: ModelStats;
        setView: (view: ModelView) => void;
        dispose: () => void;
      }
    | undefined
  >(undefined);
  const [status, setStatus] = useState("Loading model…");
  const [stats, setStats] = useState<ModelStats | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const abort = new AbortController();
    let mounted = true;
    const load = async () => {
      try {
        const response = await fetch(model.source, {
          credentials: "same-origin",
          cache: "no-store",
          signal: abort.signal,
        });
        if (!response.ok) throw new Error("The original model is unavailable.");
        const bytes = await response.arrayBuffer();
        abort.signal.throwIfAborted();
        const { mountModelViewer } = await import("./model-engine");
        const next = await mountModelViewer(
          host.current!,
          bytes,
          model.format,
          abort.signal,
        );
        if (!mounted) {
          next.dispose();
          return;
        }
        controller.current = next;
        setStats(next.stats);
        setStatus("");
      } catch (loadError) {
        if (!abort.signal.aborted) {
          setStatus("");
          setError(readableError(loadError));
        }
      }
    };
    void load();
    return () => {
      mounted = false;
      abort.abort();
      controller.current?.dispose();
      controller.current = undefined;
    };
  }, [model.format, model.source]);

  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [close]);

  const setView = (view: ModelView) => controller.current?.setView(view);
  return (
    <div className="model-viewer-backdrop" onPointerDown={close}>
      <section
        className="model-viewer"
        role="dialog"
        aria-modal="true"
        aria-label={`View ${model.name}`}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="model-viewer-format">
              <Box size={15} /> {modelFormatLabel(model.format)} model
            </span>
            <strong title={model.name}>{model.name}</strong>
          </div>
          <div className="model-viewer-header-actions">
            <a href={model.source} download={model.name}>
              <Download size={16} /> Download original
            </a>
            <button aria-label="Close model viewer" onClick={close}>
              <X size={20} />
            </button>
          </div>
        </header>
        <div className="model-viewer-stage">
          <div ref={host} className="model-viewer-canvas" />
          {status && (
            <div className="model-viewer-state" role="status">
              <LoaderCircle className="spin" size={24} /> {status}
            </div>
          )}
          {error && (
            <div className="model-viewer-state model-viewer-error" role="alert">
              <Box size={28} />
              <strong>Model preview unavailable</strong>
              <span>{error}</span>
              <a href={model.source} download={model.name}>
                Download original
              </a>
            </div>
          )}
        </div>
        <footer>
          <div className="model-view-buttons" aria-label="Model views">
            <button onClick={() => setView("iso")}>Isometric</button>
            <button onClick={() => setView("front")}>Front</button>
            <button onClick={() => setView("right")}>Right</button>
            <button onClick={() => setView("top")}>Top</button>
          </div>
          <span>
            {stats
              ? `${stats.objects.toLocaleString()} objects · ${stats.triangles.toLocaleString()} triangles`
              : "Drag to orbit · scroll to zoom · right-drag to pan"}
          </span>
        </footer>
      </section>
    </div>
  );
}
